import React, { useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";

//CSS
import "./index.scss";

// The same-origin path the nginx proxy serves the docs under.
const DOCS_BASE = "/help-center";

// Normalize a pathname for equality checks (ignore trailing slashes).
const norm = (p) => (p || "").replace(/\/+$/, "") || "/";

// "/help-center/docs/getting-started/" -> "docs/getting-started"
function docRest(pathname) {
	let rest = pathname || "";
	if (rest.startsWith(DOCS_BASE)) rest = rest.slice(DOCS_BASE.length);
	return rest.replace(/^\/+/, "").replace(/\/+$/, "");
}

// AMS splat ("docs/getting-started") -> docs URL ("/help-center/docs/getting-started")
const docUrlFromSplat = (splat) => (splat ? `${DOCS_BASE}/${splat}` : `${DOCS_BASE}/`);

// docs pathname -> AMS path ("/help/docs/getting-started")
function amsPathFromDocPathname(pathname) {
	const rest = docRest(pathname);
	return rest ? `/help/${rest}` : "/help";
}

function Help() {
	const params = useParams();
	const navigate = useNavigate();
	const iframeRef = useRef(null);

	// The part of the URL after "/help/" — the doc to open.
	const splat = params["*"] || "";

	// Capture the initial deep-link target once; the iframe's src is set from this
	// and then never re-bound (re-binding src would force a full reload).
	const initialSrcRef = useRef(docUrlFromSplat(splat));

	// Skip the parent->iframe drive on first render — the initial src already
	// points at the deep-link target, so driving it again would double-load.
	const firstRun = useRef(true);

	// Stable ref to navigate so the (long-lived) click interceptor never goes stale.
	const navigateRef = useRef(navigate);
	navigateRef.current = navigate;

	// History model — single source of truth:
	//   * Every navigation goes through the PARENT (React Router push), so each doc
	//     page is exactly one clean parent history entry with the right /help/... URL.
	//   * The iframe is ALWAYS driven with location.replace(), so it never builds its
	//     own competing history stack.
	// This is what fixes the muddled back-button titles: the old approach let the
	// iframe push its own entries (committed with a stale document.title) while the
	// parent only ever replaced a single entry.

	// Parent -> iframe: when the AMS URL changes (deep-link elsewhere, Back/Forward),
	// steer the iframe to match via replace. The "already there?" guard prevents loops.
	useEffect(() => {
		if (firstRun.current) {
			firstRun.current = false;
			return;
		}
		const iframe = iframeRef.current;
		if (!iframe) return;
		const target = docUrlFromSplat(splat);
		let current;
		try {
			current = iframe.contentWindow.location.pathname; // same-origin: allowed
		} catch (e) {
			return;
		}
		if (norm(current) !== norm(target)) {
			try {
				iframe.contentWindow.location.replace(target);
			} catch (e) {
				/* not loaded yet — the initial src covers it */
			}
		}
	}, [splat]);

	// Intercept in-iframe link clicks and route them through the parent instead, so
	// the URL bar, browser history, and tab title all stay in sync. Left untouched:
	// new-tab/download/modified clicks, external links, and same-page #anchors.
	const interceptClick = useCallback((e) => {
		if (e.defaultPrevented || e.button !== 0) return;
		if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
		const anchor = e.target.closest && e.target.closest("a[href]");
		if (!anchor) return;
		if (anchor.target && anchor.target !== "_self") return; // _blank etc.
		if (anchor.hasAttribute("download")) return;

		const iframe = iframeRef.current;
		if (!iframe) return;
		let url;
		let here;
		try {
			here = iframe.contentWindow.location;
			url = new URL(anchor.href, anchor.baseURI);
		} catch (err) {
			return;
		}
		if (url.origin !== here.origin) return; // external -> open normally
		if (!url.pathname.startsWith(DOCS_BASE)) return; // outside the docs -> leave it
		if (url.pathname === here.pathname && url.hash) return; // same-page anchor -> let it scroll

		e.preventDefault();
		navigateRef.current(amsPathFromDocPathname(url.pathname)); // push a real history entry
	}, []);

	// On each iframe load: set the tab title from the doc page, keep the URL honest as
	// a fallback, and (re)attach the click interceptor to the freshly loaded document.
	const handleLoad = useCallback(() => {
		const iframe = iframeRef.current;
		if (!iframe) return;
		let doc;
		try {
			doc = iframe.contentDocument;
		} catch (e) {
			return; // cross-origin doc link
		}
		if (!doc) return;

		// Tab title <- current doc page (docs emit "{Page} | Help Center").
		if (doc.title) document.title = doc.title;

		// Fallback reverse-sync: if the iframe got here by something we didn't
		// intercept (JS redirect, search submit, missed link), mirror the URL
		// WITHOUT adding a history entry.
		const amsPath = amsPathFromDocPathname(doc.location.pathname);
		if (norm(window.location.pathname) !== norm(amsPath)) {
			navigate(amsPath, { replace: true });
		}

		// New document on every load — attach in capture phase to beat default nav.
		doc.addEventListener("click", interceptClick, true);
	}, [navigate, interceptClick]);

	// Fallback title shown for the brief moment before the first iframe load resolves.
	useEffect(() => {
		document.title = "Help Center";
	}, []);

	return (
		<div id="help-center" className="wrapper">
			<div className="top-fold">
				<div className="container">
					<div className="container--inner">
						<h1>Help Center</h1>
					</div>
				</div>
			</div>
			<div className="container">
				<iframe
					ref={iframeRef}
					className="inner"
					title="Help Center"
					// Same-origin (friendly iframe): served by the portal nginx proxy,
					// which reverse-proxies the Help Center CloudFront distribution.
					src={initialSrcRef.current}
					onLoad={handleLoad}
				></iframe>
			</div>
		</div>
	);
}

export default Help;
